"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AsyncDocument = void 0;
const TextOperation_1 = require("./TextOperation");
const SerialQueue_1 = require("../SerialQueue");
const APPLY_SERVER_OPERATION_MAX_TRIES = 5;
/**
 * Enables code editors that asynchronously applies server operations and emits events for
 * applied server operations, AKA VSCode, to work with the OT Client.
 */
class AsyncDocument {
    queue = new SerialQueue_1.SerialQueue("async-document-server-operations");
    bufferedDocument;
    pendingServerOperations = [];
    constructor(document) {
        this.bufferedDocument = document;
    }
    get document() {
        return this.bufferedDocument;
    }
    get hasPendingServerOperations() {
        return Boolean(this.pendingServerOperations.length);
    }
    /**
     * Applies the operation to the buffered document and returns the transformed
     * operation to be applied to the OT Client
     */
    applyClientOperation(operation) {
        const prevBufferedDocument = this.bufferedDocument;
        this.bufferedDocument = operation.apply(prevBufferedDocument);
        const pendingServerOperation = this.pendingServerOperations[0];
        if (pendingServerOperation) {
            const { operation: currentServerOperation } = pendingServerOperation;
            // The operation can result in the current document, which means it should be ignored
            if (this.bufferedDocument ===
                currentServerOperation.apply(prevBufferedDocument)) {
                this.pendingServerOperations.shift();
                return;
            }
        }
        /**
         * We transform the operation with any current pending server operations,
         * guaranteeing eventual consistency between "bufferDocument" and
         * the OT document. We also transform the pending operations themselves
         * so they are applied correctly to the document when they are eventually handled
         */
        let transformedOp = operation;
        for (let x = 0; x < this.pendingServerOperations.length; x++) {
            const { operation: currentServerOperation } = this.pendingServerOperations[x];
            const [a, b] = TextOperation_1.TextOperation.transform(transformedOp, currentServerOperation);
            transformedOp = a;
            this.pendingServerOperations[x].operation = b;
        }
        return transformedOp;
    }
    /**
     * Adds the operation to the queue and calls the callback with the current
     * buffered document and the transformed operation to be applied to the
     * code editor. The callback should throw an error when applying the operation
     * to the code editor fails. The promise returned will reject after X retries
     */
    applyServerOperation(operation, cb) {
        // The operation can be transformed by intermediate changes, so we keep
        // it in a reference object for use when applying it fails
        const operationReference = { operation };
        this.pendingServerOperations.push(operationReference);
        return this.queue.add(() => {
            /**
             * We need to retry applying the operation if it fails. The reason is that VSCode will not apply
             * the incoming operation if the user happens to type something at that exact time. When we retry
             * the operation will already have been transformed by the edit the user did, so that the operation
             * is insterted at the correct position in the document
             */
            const applyOperation = async (currentTryCount) => {
                try {
                    await cb(this.bufferedDocument, operationReference.operation);
                }
                catch (error) {
                    if (currentTryCount > APPLY_SERVER_OPERATION_MAX_TRIES) {
                        throw new Error(`Not able to apply incoming operation after ${APPLY_SERVER_OPERATION_MAX_TRIES} tries: ${String(error)}`);
                    }
                    return applyOperation(++currentTryCount);
                }
            };
            return applyOperation(1);
        });
    }
}
exports.AsyncDocument = AsyncDocument;
//# sourceMappingURL=data:application/json;base64,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